import { Component, inject, signal, computed, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import {
  SidebarComponent,
  SidebarItem,
  ThemeService,
  AvatarComponent,
  DividerComponent,
  ToastContainerComponent,
  DropdownMenuComponent,
  DropdownMenuItem,
  ToastService,
  BannerComponent,
  ButtonComponent,
} from '@ams/ui-kit';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AuthService } from '../../services/auth.service';
import { AccessControlService } from '../../services/access-control.service';
import { TenantSettingsService } from '../../services/tenant-settings.service';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { PlanEntitlementService } from '../../services/plan-entitlement.service';
import { INVOICE_PAYMENT_BANNER_KEY, InvoicePaymentService } from '../../services/invoice-payment.service';
import { planFeatureNotIncludedMessage } from '../../services/plan-entitlement-constants';
import { IconService } from '../../services/icon.service';
import { FooterComponent } from './footer/footer.component';
import { GlobalSearchComponent } from '../../modules/search/global-search.component';
import { PendingMembershipBannerComponent } from '../pending-membership-banner/pending-membership-banner.component';
import { ChatbotPanelComponent } from '../chatbot-panel/chatbot-panel.component';
import { ChatbotPanelStateService } from '../../services/chatbot-panel-state.service';
import { NotificationBellComponent } from './notification-bell/notification-bell.component';
import { NotificationService } from '../../services/notification.service';

/**
 * Store-section nav items gated on View + the `storefront` env flag alone.
 * Categories and Settings are deliberately absent — they need Edit and Manage
 * respectively, so they're checked individually against their route guards'
 * `requiredLevel`. Add any new storefront screen here, or its nav entry will
 * stay visible with the feature switched off and link to a guard that bounces.
 */
const FLAG_ONLY_STOREFRONT_MENU_IDS = new Set([
  'products',
  'store-orders',
  'store-coupons',
  'store-shipping',
  'store-reports',
]);

/**
 * Grouping rows that own no module of their own — containers whose only job is to hold
 * children. Their visibility follows the children (filterItemsRecursive drops a parent once
 * every child is filtered out), so they deliberately skip the module check: there is no
 * single module that would answer correctly, and picking one would hide the whole group
 * whenever that one module is denied — Engagement would vanish for someone holding Tasks
 * but not Pipelines.
 *
 * Listed explicitly rather than left to canViewMenuItem's unmapped-id fail-open, so the
 * intent is on the page and a reader isn't left wondering whether the omission from
 * MENU_MODULE_MAP was deliberate. Containers whose id names a real module — Memberships,
 * Billing, CMS, Settings — are mapped there and stay gated on it.
 */
const NAV_GROUP_MENU_IDS = new Set([
  'engagement',
  'store',
  'reports',
  'administration',
]);

/** Tenant/env state the nav visibility rules read, threaded through every depth. */
interface NavFilterContext {
  settingsReady: boolean;
  allowChapters: boolean;
  allowSubChapters: boolean;
  chaptersAccessLevel: 'view' | 'edit' | 'manage' | null;
  storefrontEnabled: boolean;
}

// =============================================================================
// Shell Component
// Main application layout with sidebar and header
// =============================================================================
@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SidebarComponent,
    AvatarComponent,
    DividerComponent,
    ToastContainerComponent,
    FontAwesomeModule,
    FooterComponent,
    DropdownMenuComponent,
    GlobalSearchComponent,
    PendingMembershipBannerComponent,
    ChatbotPanelComponent,
    NotificationBellComponent,
    BannerComponent,
    ButtonComponent,
  ],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
})
export class ShellComponent implements OnInit {
  readonly themeService = inject(ThemeService);
  readonly authService = inject(AuthService); // Triggers AuthService initialization
  readonly accessControl = inject(AccessControlService);
  readonly tenantSettings = inject(TenantSettingsService);
  readonly features = inject(FeatureFlagService);
  // Per-TENANT plan state. Kept beside `features` (per-ENVIRONMENT flags) rather
  // than merged into it — see PlanEntitlementService for why the two axes stay apart.
  readonly planEntitlements = inject(PlanEntitlementService);
  // Pay-now action beside the invoice banner. Public because the shared template
  // binds it directly; the same pair is declared on PortalShellComponent, which
  // reuses this template and is type-checked against it.
  readonly invoicePayment = inject(InvoicePaymentService);
  readonly invoicePaymentBannerKey = INVOICE_PAYMENT_BANNER_KEY;

  /**
   * Membora staff on the reserved platform tenant. They administer OTHER
   * associations and hold no plan of their own, so plan-scoped notices are
   * meaningless to them — see the degraded banner in the template.
   */
  readonly isPlatformTenant = computed(
    () => !!this.authService.currentUser()?.isPlatformTenant,
  );
  readonly iconService = inject(IconService);
  private readonly toastService = inject(ToastService);
  readonly chatbotPanelState = inject(ChatbotPanelStateService);
  private readonly router = inject(Router);
  private readonly notificationService = inject(NotificationService);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(e => (e as NavigationEnd).urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  ngOnInit(): void {
    this.notificationService.loadBellFeed();
  }

  readonly sidebarCollapsed = signal<boolean>(false);
  readonly copilotOpen = signal<boolean>(false);
  readonly mobileSidebarOpen = signal<boolean>(false);
  readonly userMenuOpen = signal<boolean>(false);
  readonly branding = this.tenantSettings.branding;

  // Portal mode — always false for admin shell (PortalShellComponent overrides this)
  readonly portalMode = signal<boolean>(false);
  readonly orgName = computed(() => '');

  readonly activeOrgName = computed(() => {
    const org = this.authService.activeOrg();
    if (org) return org.orgName ?? null;
    // Tenant-wide users: show the name from their first assignment (HQ org)
    const user = this.authService.currentUser();
    if (!user?.orgRoleAssignments?.length) return null;
    return user.orgRoleAssignments[0].orgName ?? null;
  });

  // Sidebar brand badge: the active org's name when the user is operating in a
  // non-association (chapter/committee/member) context. Empty at association level.
  readonly chapterBadge = computed(() => {
    const org = this.authService.activeOrg();
    if (org && (org.orgStructure ?? '') !== 'ROOT') return org.orgName ?? '';
    return '';
  });

  // Org switcher in user menu:
  // - Tenant-only users (only ROOT assignment): hidden — they stay tenant context
  // - Single non-tenant assignment: hidden — context is auto-fixed
  // - Multi-org users OR hybrid users (tenant + any non-ROOT assignment):
  //   shown so they can deliberately switch into chapter/member mode.
  //   In scope-typed mode, switching to a chapter suppresses the tenant role's
  //   grants — a deliberate "lower privileges to chapter scope" action.
  readonly showOrgSwitcher = computed(() => {
    const orgs = this.authService.userOrgs();
    if (orgs.length <= 1) return false;
    return true;
  });

  readonly userMenuItems = computed<DropdownMenuItem[]>(() => {
    const items: DropdownMenuItem[] = [
      { label: 'My Account', action: 'profile', section: this.currentUserName(), icon: this.iconService.getIcon('user-menu') },
      { label: 'Notification settings', action: 'notification-settings', icon: this.iconService.getIcon('settings'), active: this.currentUrl() === '/notification-settings' },
    ];

    if (this.showOrgSwitcher()) {
      const orgs = this.authService.userOrgs();
      const activeOrgId = this.authService.activeOrgId();

      const groups: { label: string; icon: unknown; structures: string[] }[] = [
        { label: 'Association', icon: this.iconService.getIcon('dashboard'), structures: ['ROOT'] },
        { label: 'Chapters', icon: this.iconService.getIcon('chapters'), structures: ['CHAPTER'] },
        { label: 'Members', icon: this.iconService.getIcon('memberships'), structures: ['EXTERNAL', 'COMMITTEE'] },
      ];

      for (const group of groups) {
        const groupOrgs = orgs.filter(o => group.structures.includes(o.orgStructure ?? ''));
        if (groupOrgs.length === 0) continue;

        groupOrgs.forEach((org, i) => {
          items.push({
            label: org.orgName || org.orgId || 'Organization',
            description: org.roleNames?.join(', ') || undefined,
            action: `org:${org.orgId}`,
            active: activeOrgId === org.orgId,
            scrollable: true,
            ...(i === 0 ? { dividerBefore: true, section: group.label, sectionIcon: group.icon } : {}),
          });
        });
      }
    }

    items.push({ label: 'Log out', action: 'logout', variant: 'destructive', icon: this.iconService.getIcon('logout'), dividerBefore: true });

    return items;
  });

  readonly currentUserName = computed(() => {
    const user = this.authService.currentUser();
    if (!user) return 'User';
    return `${user.firstName} ${user.lastName}`.trim() || user.email || 'User';
  });

  // Check if user is a Membora system administrator
  readonly isMemboraAdmin = computed(() => {
    const user = this.authService.currentUser();
    return user?.email?.toLowerCase().endsWith('@membora.ai') || false;
  });

  // Compute user's primary role label from their org role assignments
  readonly userRoleLabel = computed(() => {
    const user = this.authService.currentUser();

    if (this.isMemboraAdmin()) return 'System Admin';
    if (!user?.orgRoleAssignments?.length) return 'User';

    const activeOrgId = this.authService.activeOrgId();

    // Get roles for the active org context
    // - Tenant-wide user (activeOrgId = null): show root org / all roles
    // - Org-scoped user: show only roles at the active org
    const roleNames = new Set<string>();
    for (const a of user.orgRoleAssignments) {
      if (a.orgId === activeOrgId) {
        a.roleNames?.forEach(name => roleNames.add(name));
      }
    }

    if (roleNames.size === 0) return 'User';

    const names = [...roleNames];
    if (names.length === 1) return names[0];
    return `${names[0]} + ${names.length - 1} more`;
  });

  // Filter navigation items based on access control and tenant settings
  readonly filteredNavItems = computed(() => {
    // Membora admins get special navigation
    if (this.isMemboraAdmin()) {
      return this.systemAdminNavigationItems;
    }

    // Read signals to establish dependencies
    const access = this.accessControl.effectiveAccess();
    const settingsReady = this.tenantSettings.settingsReady();
    const allowChapters = this.tenantSettings.allowChapters();
    const allowSubChapters = this.tenantSettings.allowSubChapters();
    const chaptersAccessLevel = this.accessControl.getAccessLevel('tenant.chapters');
    const saasLogicEnabled = this.features.saasLogicEnabled();
    const storefrontEnabled = this.features.storefrontEnabled();

    const items = this.filterNavItems(
      this.allNavigationItems,
      access,
      settingsReady,
      allowChapters,
      allowSubChapters,
      chaptersAccessLevel,
      storefrontEnabled,
    );

    // Feature-gated nav: when SaaSLogic billing is off, the Billing entries stay
    // visible but locked (non-navigable + lock tooltip) rather than disappearing.
    const envLocked = saasLogicEnabled ? items : this.lockBillingNav(items);

    // Plan-gated nav, applied AFTER the environment pass. Two separate axes sharing one
    // presentation: an environment flag is per-deployment and nothing the customer can
    // buy their way out of; a plan exclusion is per-tenant and upgrading fixes it. They
    // are computed from different sources and worded differently — only the disabled +
    // lockMessage rendering is shared.
    return this.lockPlanGatedNav(envLocked);
  });

  // Marks nav entries whose module needs a plan feature the tenant does not have. Locked
  // rather than hidden, deliberately: an association discovers Chapters exists by seeing
  // it greyed out with an upgrade hint. Recurses for nested sections and maps to new
  // objects so the static source array is never mutated.
  private lockPlanGatedNav(items: SidebarItem[]): SidebarItem[] {
    const walk = (list: SidebarItem[]): SidebarItem[] =>
      list.map((item) => {
        const withChildren = item.children?.length
          ? { ...item, children: walk(item.children) }
          : item;

        const lockedFeature = this.accessControl.planLockedFeatureForMenuItem(item.id);
        if (!lockedFeature) return withChildren;

        return {
          ...withChildren,
          disabled: true,
          lockMessage: planFeatureNotIncludedMessage(lockedFeature),
        };
      });
    return walk(items);
  }

  // Returns a copy of the nav tree with the Billing section's leaf items marked
  // disabled + lockMessage. Recurses because 'billing' is nested under the
  // Memberships section, not a top-level item. Maps to new objects so the static
  // source array isn't mutated.
  private lockBillingNav(items: SidebarItem[]): SidebarItem[] {
    const message = this.features.unavailableMessage();
    const walk = (list: SidebarItem[]): SidebarItem[] =>
      list.map((item) => {
        if (item.id === 'billing' && item.children?.length) {
          return {
            ...item,
            children: item.children.map((c) => ({ ...c, disabled: true, lockMessage: message })),
          };
        }
        // Financials report draws on the same SaaSLogic billing data — lock it
        // alongside the Billing entries when billing is off.
        if (item.id === 'financials-report') {
          return { ...item, disabled: true, lockMessage: message };
        }
        if (item.children?.length) {
          return { ...item, children: walk(item.children) };
        }
        return item;
      });
    return walk(items);
  }

  private filterNavItems(
    items: SidebarItem[],
    _access: Map<string, string>,
    settingsReady: boolean,
    allowChapters: boolean,
    allowSubChapters: boolean,
    chaptersAccessLevel: 'view' | 'edit' | 'manage' | null,
    storefrontEnabled: boolean,
  ): SidebarItem[] {
    // _access param ensures signal dependency is tracked
    return this.filterItemsRecursive(items, {
      settingsReady, allowChapters, allowSubChapters, chaptersAccessLevel, storefrontEnabled,
    })
      .filter((item, index, arr) => {
        // Remove orphaned dividers/sections (no visible items after them)
        if (item.divider || item.section) {
          const nextItem = arr[index + 1];
          return nextItem && !nextItem.divider && !nextItem.section;
        }
        return true;
      });
  }

  /**
   * Applies the visibility rules at every depth. One recursive pass rather than a
   * top-level filter plus a separate child filter: the nav sections are collapsible
   * parents now, so items carrying feature-flag and access-level rules (the whole
   * Store group, Settings) sit at depth 1+. Filtering those only at the top level
   * would silently drop every one of those gates.
   */
  private filterItemsRecursive(items: SidebarItem[], ctx: NavFilterContext): SidebarItem[] {
    return items
      .filter((item) => this.isNavItemVisible(item, ctx))
      .map((item) => {
        if (item.children) {
          const filteredChildren = this.filterItemsRecursive(item.children, ctx);
          // Hide a parent whose children are all inaccessible
          if (filteredChildren.length === 0) return null;
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      .filter((item): item is SidebarItem => item !== null);
  }

  private isNavItemVisible(item: SidebarItem, ctx: NavFilterContext): boolean {
    const { settingsReady, allowChapters, allowSubChapters, chaptersAccessLevel, storefrontEnabled } = ctx;

    // Skip explicitly hidden items
    if (item.hidden) return false;

    // Always show dividers and sections if they have visible siblings
    if (item.divider || item.section) return true;

    // View is enough to see (and open) Settings — the pages themselves gate
    // saving on tenant.settings/manage. Matches the /settings route guard.
    if (this.isSettingsMenuItem(item)) {
      return this.accessControl.hasAccess('tenant.settings', 'view');
    }

    // Categories admin requires Edit/Manage (not just View) on Storefront,
    // plus the storefront feature flag — hidden entirely otherwise (not
    // locked-with-tooltip like the SaaSLogic billing gate).
    if (item.id === 'store-categories') {
      return storefrontEnabled && this.accessControl.hasAccess('tenant.storefront', 'edit');
    }

    // Settings writes config, so it requires Manage specifically (not just
    // View/Edit) — matches the /store/settings route guard's
    // requiredLevel: 'manage'. Hidden entirely otherwise, same as Categories.
    if (item.id === 'store-settings') {
      return storefrontEnabled && this.accessControl.hasAccess('tenant.storefront', 'manage');
    }

    // Storefront nav entry requires both module access (checked below via
    // canViewMenuItem) and the storefront feature flag — the route/backend
    // are gated on both, so the nav entry must hide on either being off.
    if (item.id === 'storefront' && !storefrontEnabled) {
      return false;
    }

    // Chapters directory is tenant-scope; active-context filter ensures
    // chapter-scoped admins don't see it (their tenant.X grants are
    // suppressed when in chapter context). Tenant admins with chapters:view
    // see it through the standard canViewMenuItem check below.
    if (item.id === 'chapters' && settingsReady) {
      if (!allowChapters) {
        return false;
      }

      if (!allowSubChapters && chaptersAccessLevel === 'edit') {
        return false;
      }
    }

    // Storefront screens needing only View + the env flag. (Categories and
    // Settings are handled above because they additionally require Edit and
    // Manage respectively.) Every one of these routes carries
    // `featureFlag: 'storefront'`, so the nav entry has to hide on the same
    // signal or it links somewhere the guard will bounce.
    //
    // One list rather than a check per screen: this had already drifted into
    // three separate conditions and store-shipping had been missed entirely,
    // leaving Shipping rates visible with the storefront flag off.
    if (FLAG_ONLY_STOREFRONT_MENU_IDS.has(item.id) && !storefrontEnabled) {
      return false;
    }

    // A pure grouping row defers to its children — see NAV_GROUP_MENU_IDS.
    if (NAV_GROUP_MENU_IDS.has(item.id)) return true;

    // Check access - hide if user doesn't have view access to the module
    return this.accessControl.canViewMenuItem(item.id);
  }

  private isSettingsMenuItem(item: SidebarItem): boolean {
    return item.id === 'settings'
      || item.route?.startsWith('/settings/') === true;
  }

  // All navigation items (filtered by access control)
  readonly allNavigationItems: SidebarItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      route: '/dashboard',
      icon: this.iconService.getIcon('dashboard'),
    },
    {
      id: 'memberships',
      label: 'Memberships',
      icon: this.iconService.getIcon('memberships'),
      children: [
        { id: 'plans', label: 'Plans', route: '/memberships/plans', icon: this.iconService.getIcon('document') },
        { id: 'accounts', label: 'Accounts', route: '/memberships/accounts', icon: this.iconService.getIcon('membership') },
        { id: 'applications', label: 'Applications', route: '/memberships/applications', icon: this.iconService.getIcon('document') },
        {
          id: 'billing',
          label: 'Billing',
          icon: this.iconService.getIcon('billing'),
          children: [
            { id: 'accounts', label: 'Accounts', route: '/billing/accounts', icon: this.iconService.getIcon('chapters') },
            { id: 'invoices', label: 'Invoices', route: '/billing/invoices', icon: this.iconService.getIcon('document') },
            { id: 'coupons', label: 'Plan Coupon', route: '/billing/coupons', icon: this.iconService.getIcon('billing') },
          ],
        },
      ],
    },
    {
      id: 'chapters',
      label: 'Chapters',
      route: '/chapters',
      icon: this.iconService.getIcon('chapters'),
    },
    {
      id: 'events',
      label: 'Events',
      route: '/events',
      icon: this.iconService.getIcon('events'),
    },
    {
      id: 'calendar',
      label: 'Calendar',
      route: '/calendar',
      icon: this.iconService.getIcon('calendar'),
    },
    { id: 'divider-engagement', label: '', divider: true },
    // Sections below are collapsible grouping rows rather than a flat run under a
    // section caption, so each behaves like Memberships: chevron, indented children,
    // one open/closed state. `hideSubmenuGuide` drops the vertical rule beside the
    // children — at this level it would read as nesting the section, not relating
    // siblings. Memberships deliberately keeps its guide.
    {
      id: 'engagement',
      label: 'Engagement',
      icon: this.iconService.getIcon('engagement'),
      hideSubmenuGuide: true,
      children: [
        { id: 'pipelines', label: 'Pipelines', route: '/engagement/pipelines', icon: this.iconService.getIcon('pipelines') },
        { id: 'tasks', label: 'Tasks', route: '/engagement/tasks', icon: this.iconService.getIcon('tasks') },
        { id: 'activities', label: 'Activities', route: '/engagement/activities', icon: this.iconService.getIcon('activities') },
        { id: 'groups', label: 'Groups', route: '/groups', icon: this.iconService.getIcon('groups') },
        { id: 'resources', label: 'Resources', route: '/resources', icon: this.iconService.getIcon('resources') },
      ],
    },
    { id: 'divider-store', label: '', divider: true },
    {
      id: 'store',
      label: 'Store',
      icon: this.iconService.getIcon('storefront'),
      hideSubmenuGuide: true,
      children: [
        { id: 'storefront', label: 'Overview', route: '/store', icon: this.iconService.getIcon('storefront') },
        { id: 'products', label: 'Products', route: '/store/products', icon: this.iconService.getIcon('basket') },
        { id: 'store-categories', label: 'Categories', route: '/store/categories', icon: this.iconService.getIcon('folder') },
        // #21791 — the route (`store/orders`) and its guards already existed; only
        // the nav entry was missing, so the screen was reachable by URL alone.
        { id: 'store-orders', label: 'Orders', route: '/store/orders', icon: this.iconService.getIcon('storefront-orders') },
        // #21795 — MENU_MODULE_MAP maps 'store-coupons' -> 'tenant.storefront'.
        { id: 'store-coupons', label: 'Product Coupon', route: '/store/coupons', icon: this.iconService.getIcon('coupons') },
        // #25924 — MENU_MODULE_MAP maps 'store-fees' -> 'tenant.storefront'.
        { id: 'store-fees', label: 'Fees', route: '/store/fees', icon: this.iconService.getIcon('fees') },
        // #21796 — MENU_MODULE_MAP maps 'store-shipping' -> 'tenant.storefront'.
        { id: 'store-shipping', label: 'Shipping rates', route: '/store/shipping-rates', icon: this.iconService.getIcon('shipping') },
        { id: 'store-reports', label: 'Reports', route: '/store/reports', icon: this.iconService.getIcon('chart') },
        // Kept last — config lives below the operational and reporting screens.
        { id: 'store-settings', label: 'Settings', route: '/store/settings', icon: this.iconService.getIcon('settings') },
      ],
    },
    { id: 'divider-cms', label: '', divider: true },
    {
      id: 'cms',
      label: 'CMS',
      icon: this.iconService.getIcon('cms'),
      hideSubmenuGuide: true,
      children: [
        { id: 'cms-models', label: 'Models', route: '/cms/models', icon: this.iconService.getIcon('cms') },
        { id: 'cms-content', label: 'Content', route: '/cms/content', icon: this.iconService.getIcon('document') },
      ],
    },
    { id: 'divider-reports', label: '', divider: true },
    {
      id: 'reports',
      label: 'Reports',
      icon: this.iconService.getIcon('chart'),
      hideSubmenuGuide: true,
      children: [
        { id: 'membership-report', label: 'Membership', route: '/reports/membership', icon: this.iconService.getIcon('memberships') },
        { id: 'events-report', label: 'Events', route: '/reports/events', icon: this.iconService.getIcon('events') },
        { id: 'financials-report', label: 'Financials', route: '/reports/financials', icon: this.iconService.getIcon('billing') },
      ],
    },
    { id: 'divider-admin', label: '', divider: true },
    {
      id: 'administration',
      label: 'Administration',
      icon: this.iconService.getIcon('permissions'),
      hideSubmenuGuide: true,
      children: [
        { id: 'staff', label: 'Staff', route: '/users', icon: this.iconService.getIcon('staff') },
        { id: 'roles', label: 'Roles', route: '/roles', icon: this.iconService.getIcon('roles') },
        { id: 'permissions', label: 'Permissions', route: '/permissions', icon: this.iconService.getIcon('permissions'), hidden: true },
        { id: 'customizations', label: 'Customizations', route: '/customizations', icon: this.iconService.getIcon('customizations') },
        {
          id: 'settings',
          label: 'Settings',
          icon: this.iconService.getIcon('settings'),
          children: [
            { id: 'general', label: 'General', route: '/settings/general', icon: this.iconService.getIcon('settings') },
            { id: 'auth', label: 'Authentication', route: '/settings/auth', icon: this.iconService.getIcon('permissions') },
            { id: 'appearance', label: 'Appearance', route: '/settings/appearance', icon: this.iconService.getIcon('theme-light') },
            { id: 'subscription', label: 'Subscription', route: '/settings/subscription', icon: this.iconService.getIcon('billing') },
          ],
        },
      ],
    },
  ];

  // Navigation items for Membora system administrators
  readonly systemAdminNavigationItems: SidebarItem[] = [
    {
      id: 'system-dashboard',
      label: 'Dashboard',
      route: '/system-admin',
      icon: this.iconService.getIcon('dashboard'),
    },
    { id: 'divider-platform', label: '', divider: true },
    { id: 'section-platform', label: '', section: 'Platform Management' },
    {
      id: 'tenants',
      label: 'Tenants',
      route: '/system-admin/tenants',
      icon: this.iconService.getIcon('chapters'),
    },
    {
      id: 'platform-users',
      label: 'Platform Users',
      route: '/system-admin/users',
      icon: this.iconService.getIcon('memberships'),
    },
    {
      // Membora's own product plans, the ones associations subscribe to. Distinct
      // from /memberships/plans, which is a tenant's own member plans.
      id: 'platform-plans',
      label: 'Subscription Plans',
      route: '/system-admin/plans',
      icon: this.iconService.getIcon('memberships'),
    },
    {
      id: 'legal-documents',
      label: 'Legal Documents',
      route: '/system-admin/legal',
      icon: this.iconService.getIcon('document'),
    },
    {
      id: 'roles',
      label: 'Roles',
      route: '/roles',
      icon: this.iconService.getIcon('roles'),
    },
    { id: 'divider-system', label: '', divider: true },
    { id: 'section-system', label: '', section: 'System' },
    {
      id: 'system-health',
      label: 'System Health',
      route: '/system-admin/health',
      icon: this.iconService.getIcon('activities'),
    },
    {
      id: 'audit-logs',
      label: 'Audit Logs',
      route: '/system-admin/audit-logs',
      icon: this.iconService.getIcon('document'),
    },
    {
      id: 'components',
      label: 'Components',
      route: '/settings/components',
      icon: this.iconService.getIcon('customizations'),
    },
    { id: 'divider-dashboard-admin', label: '', divider: true },
    { id: 'section-dashboard-admin', label: '', section: 'Dashboard Management' },
    {
      id: 'sa-dashboard-templates',
      label: 'Templates',
      route: '/dashboard-admin/templates',
      icon: this.iconService.getIcon('template'),
    },
    {
      id: 'sa-widget-definitions',
      label: 'Widget Definitions',
      route: '/dashboard-admin/widget-definitions',
      icon: this.iconService.getIcon('widgets'),
    },
  ];

  readonly logoUrl = computed(() => this.themeService.currentLogo());
  readonly faviconUrl = computed(() => this.themeService.favicon());
  readonly logoAlt = computed(() => this.tenantSettings.orgName() || 'Membora AMS');

  readonly associationName = computed(
    () => this.tenantSettings.generalSettings().orgName || 'Membora AMS',
  );

  onSidebarCollapse(collapsed: boolean): void {
    this.sidebarCollapsed.set(collapsed);
  }

  toggleCopilot(): void {
    this.copilotOpen.update((v) => !v);
  }

  toggleChatbot(): void {
    this.chatbotPanelState.toggle();
  }

  toggleUserMenu(): void {
    this.userMenuOpen.update((v) => !v);
  }

  closeUserMenu(): void {
    this.userMenuOpen.set(false);
  }

  onProfileClick(): void {
    this.closeUserMenu();
    this.router.navigate(['/my-account']);
  }

  onAccountClick(): void {
    this.closeUserMenu();
    alert(
      'Todo: send user to an account screen where the user can manage their password, etc..',
    );
  }

  toggleMobileSidebar(): void {
    this.mobileSidebarOpen.update((v) => !v);
  }

  closeMobileSidebar(): void {
    this.mobileSidebarOpen.set(false);
  }


  switchPersona(): void {
    this.router.navigate(['/launcher']);
  }

  onUserMenuAction({ action }: { action: string }): void {
    switch (action) {
      case 'profile':
        this.onProfileClick();
        break;
      case 'notification-settings':
        this.closeUserMenu();
        this.router.navigate(['/notification-settings']);
        break;
      case 'settings':
        this.onAccountClick();
        break;
      case 'logout':
        this.logout();
        break;
      default:
        if (action.startsWith('org:')) {
          const orgId = action === 'org:__all__' ? null : action.substring(4);
          const selectedOrg = this.authService.userOrgs().find((org) => org.orgId === orgId);

          this.closeUserMenu();

          if (selectedOrg?.orgStructure === 'EXTERNAL' && orgId) {
            this.authService.handoffToMemberPortal(orgId).catch((err) => {
              const message = err instanceof Error ? err.message : 'We could not open the Member Portal right now.';
              this.toastService.error(message);
            });
            return;
          }

          this.authService.switchOrg(orgId).then(() => {
            this.router.navigate(['/dashboard']);
          });
        }
    }
  }

  logout(): void {
    this.authService.logout();
  }
}
